import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiArrowLeft, FiPlus, FiSearch, FiFilter, FiTarget, FiAlertTriangle, 
  FiCheckCircle, FiClock, FiMoreVertical, FiTrash2, FiEdit2, FiSave, FiX 
} = FiIcons;

const RiskAssessmentList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  
  // Mock data for risk assessments
  const [assessments, setAssessments] = useState([
    { id: 1, title: 'Arbeid i høyden - Lagerhall B', date: '2024-05-15', author: 'Ola Nordmann', status: 'active', risks: 3, riskLevel: 'high' },
    { id: 2, title: 'Kjemikaliehåndtering Renhold', date: '2024-04-10', author: 'Kari Hansen', status: 'completed', risks: 5, riskLevel: 'medium' },
    { id: 3, title: 'Ergonomi Kontorlandskap', date: '2024-02-20', author: 'Per Olsen', status: 'review', risks: 2, riskLevel: 'low' }
  ]);

  const [newAssessment, setNewAssessment] = useState({ title: '', description: '', department: '' });

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'review': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'active': return 'Aktiv';
      case 'completed': return 'Fullført';
      case 'review': return 'Til gjennomgang';
      default: return status;
    }
  };

  const getRiskLevelColor = (level) => {
    switch(level) {
      case 'high': return 'text-red-600 font-bold';
      case 'medium': return 'text-orange-500 font-medium';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const handleCreate = (e) => {
    e.preventDefault();
    const newId = assessments.length + 1;
    const assessment = {
      id: newId,
      title: newAssessment.title,
      date: new Date().toISOString().split('T')[0],
      author: 'Meg (Demo)',
      status: 'active',
      risks: 0,
      riskLevel: 'low'
    };
    setAssessments([assessment, ...assessments]);
    setShowModal(false);
    setNewAssessment({ title: '', description: '', department: '' });
  };

  const handleDelete = (id) => {
    if(window.confirm('Er du sikker på at du vil slette denne risikovurderingen?')) {
      setAssessments(assessments.filter(a => a.id !== id));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/company/dashboard')} 
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiArrowLeft} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Risikovurderinger</h1>
                <p className="text-sm text-gray-500">Oversikt og administrasjon av risiko</p>
              </div>
            </div>
            <button 
              onClick={() => setShowModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <SafeIcon icon={FiPlus} /> Ny risikovurdering
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-col md:flex-row gap-4 justify-between items-center">
          <div className="relative w-full md:w-96">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Søk i risikovurderinger..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <button className="px-4 py-2 border border-gray-300 rounded-lg flex items-center gap-2 hover:bg-gray-50 text-sm">
              <SafeIcon icon={FiFilter} /> Filter
            </button>
          </div>
        </div>

        {/* Assessments List */}
        <div className="grid gap-4">
          {assessments.filter(a => a.title.toLowerCase().includes(searchTerm.toLowerCase())).map((assessment) => (
            <motion.div 
              key={assessment.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
                    <SafeIcon icon={FiTarget} className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">{assessment.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1"><SafeIcon icon={FiClock} /> {assessment.date}</span>
                      <span className="flex items-center gap-1"><SafeIcon icon={FiEdit2} /> {assessment.author}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 uppercase">Risikofaktorer</p>
                    <p className="font-bold text-gray-900">{assessment.risks}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 uppercase">Risikonivå</p>
                    <p className={getRiskLevelColor(assessment.riskLevel)}>
                      {assessment.riskLevel.toUpperCase()}
                    </p>
                  </div>
                  <div className="text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(assessment.status)}`}>
                      {getStatusText(assessment.status)}
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      <SafeIcon icon={FiEdit2} />
                    </button>
                    <button onClick={() => handleDelete(assessment.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {assessments.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiTarget} className="text-gray-400 text-2xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">Ingen risikovurderinger</h3>
            <p className="text-gray-500">Begynn med å opprette en ny risikovurdering for din bedrift.</p>
          </div>
        )}
      </div>

      {/* New Assessment Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">Ny Risikovurdering</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              
              <form onSubmit={handleCreate} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tittel på vurdering *</label>
                  <input 
                    type="text" 
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="F.eks. Arbeid i høyden"
                    value={newAssessment.title}
                    onChange={e => setNewAssessment({...newAssessment, title: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Avdeling/Område</label>
                  <input 
                    type="text" 
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="F.eks. Lager, Produksjon..."
                    value={newAssessment.department}
                    onChange={e => setNewAssessment({...newAssessment, department: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse</label>
                  <textarea 
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="Kort beskrivelse av hva som skal vurderes..."
                    value={newAssessment.description}
                    onChange={e => setNewAssessment({...newAssessment, description: e.target.value})}
                  />
                </div>

                <div className="pt-4 flex gap-3">
                  <button 
                    type="button" 
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Avbryt
                  </button>
                  <button 
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiPlus} /> Opprett
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RiskAssessmentList;